/**
 * InvoiceSummary - Affiche le résumé de la facture à payer
 * Design System v2 - Style Landing (page publique)
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Building2, User } from 'lucide-react';
import type { PaymentOptions } from '../types/payment.types';

interface InvoiceSummaryProps {
  invoice: PaymentOptions['invoice'];
  formatCurrency: (amount: number, currency?: string) => string;
}

export const InvoiceSummary: React.FC<InvoiceSummaryProps> = ({
  invoice,
  formatCurrency,
}) => {
  const { t } = useTranslation();
  const amountToPay = invoice.remainingAmount > 0
    ? invoice.remainingAmount
    : invoice.totalTTC;

  return (
    <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20">
      {/* En-tête avec logo et numéro */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-lime-500/20 flex items-center justify-center">
            <FileText className="w-7 h-7 text-lime-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">
              {t('publicPayment.invoice.title', { number: invoice.number })}
            </h2>
            <p className="text-white/60 text-sm">
              {invoice.status === 'PAID' ? t('publicPayment.invoice.paid') : t('publicPayment.invoice.pending')}
            </p>
          </div>
        </div>
      </div>

      {/* Informations entreprise et client */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Entreprise émettrice */}
        {invoice.Company && (
          <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-lime-400" />
              <span className="text-white/60 text-sm">{t('publicPayment.invoice.issuer')}</span>
            </div>
            <p className="text-white font-medium">{invoice.Company.name}</p>
          </div>
        )}

        {/* Client */}
        {invoice.Client && (
          <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-lime-400" />
              <span className="text-white/60 text-sm">{t('publicPayment.invoice.client')}</span>
            </div>
            <p className="text-white font-medium">{invoice.Client.companyName}</p>
            {invoice.Client.email && (
              <p className="text-white/60 text-sm">{invoice.Client.email}</p>
            )}
          </div>
        )}
      </div>

      {/* Montant à payer */}
      <div className="bg-gradient-to-r from-lime-500/20 to-lime-400/10 rounded-2xl p-6 border border-lime-400/30">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/60 text-sm mb-1">{t('publicPayment.invoice.amountToPay')}</p>
            <p className="text-3xl font-bold text-white">
              {formatCurrency(amountToPay, invoice.currencyCode)}
            </p>
          </div>
          {invoice.remainingAmount > 0 && invoice.remainingAmount < invoice.totalTTC && (
            <div className="text-right">
              <p className="text-white/40 text-sm line-through">
                {formatCurrency(invoice.totalTTC, invoice.currencyCode)}
              </p>
              <p className="text-lime-400 text-sm">{t('publicPayment.invoice.remaining')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceSummary;
